export const DEBOUNCE_DELAY = 500;

/**
 * Creates a debounced version of the provided function that delays invoking it
 * until after `delay` milliseconds have elapsed since the last invocation.
 *
 * @param {(...args: any[]) => void} fn - The function to debounce
 * @param {number} [delay] - The debounce delay in milliseconds
 * @returns {{ (...args: any[]): void; cancel: () => void }} The debounced function with a cancel method
 */
export function debounce(fn, delay = DEBOUNCE_DELAY) {
    /** @type {ReturnType<typeof setTimeout> | null} */
    let timer = null;

    /**
     * Invoke the debounced function after the delay elapses.
     *
     * @param {any[]} args - Arguments to forward to the debounced function.
     */
    const debounced = (...args) => {
        if (timer) {
            clearTimeout(timer);
        }

        timer = setTimeout(() => {
            fn(...args);
            timer = null;
        }, delay);
    };

    /**
     * Cancels any pending debounced invocation.
     *
     * @returns {void}
     */
    debounced.cancel = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    return debounced;
}

/**
 * Parses the inner text of a DOM element as an integer, returning 0 if parsing fails.
 *
 * @param {HTMLElement | null | undefined} field - The DOM element whose innerText to parse
 * @returns {number} The parsed integer value, or 0 if the element is missing or not a number
 */
export const getParsedValue = (field) =>
    parseInt(field?.innerText ?? "", 10) || 0;

/**
 * @typedef {object} CountdownTimeConfig
 * @property {number} [days] - Number of days
 * @property {number} [hours] - Number of hours
 * @property {number} [minutes] - Number of minutes
 * @property {number} [seconds] - Number of seconds
 */

/**
 * @typedef {object} CountdownFields
 * @property {HTMLElement} [days] - Element displaying days
 * @property {HTMLElement} hours - Element displaying hours
 * @property {HTMLElement} minutes - Element displaying minutes
 * @property {HTMLElement} [seconds] - Element displaying seconds
 */

/**
 * A countdown timer that updates DOM elements every second.
 */
export class CountdownTimer {
    /**
     * Creates a new CountdownTimer instance.
     *
     * @param {CountdownTimeConfig | number} timeConfig - Either a config object with time parts or total seconds
     * @param {CountdownFields} fields - DOM elements to update with the countdown values
     */
    constructor(timeConfig, fields) {
        if (typeof timeConfig === "object" && timeConfig !== null) {
            const {
                days = 0,
                hours = 0,
                minutes = 0,
                seconds = 0,
            } = timeConfig;

            this.totalSeconds =
                days * 86400 + hours * 3600 + minutes * 60 + seconds;
        } else {
            this.totalSeconds = timeConfig;
        }

        this.fields = fields;
        /** @type {ReturnType<typeof setInterval> | null} */
        this.timerId = null;
    }

    /**
     * Starts the countdown, updating the field elements every second.
     *
     * @returns {void}
     */
    start() {
        this.timerId = setInterval(() => {
            if (this.totalSeconds <= 0) {
                clearInterval(
                    /** @type {ReturnType<typeof setInterval>} */ (
                        this.timerId
                    ),
                );
                return;
            }

            this.totalSeconds--;

            if (this.fields.days) {
                const days = Math.floor(this.totalSeconds / 86400);
                const hours = Math.floor((this.totalSeconds % 86400) / 3600);
                const minutes = Math.floor((this.totalSeconds % 3600) / 60);
                this.fields.days.innerText = String(days);
                this.fields.hours.innerText = String(hours);
                this.fields.minutes.innerText = String(minutes);
            } else {
                const hours = Math.floor(this.totalSeconds / 3600);
                const minutes = Math.floor((this.totalSeconds % 3600) / 60);
                const seconds = this.totalSeconds % 60;
                this.fields.hours.innerText = String(hours);
                this.fields.minutes.innerText = String(minutes);

                if (this.fields.seconds) {
                    this.fields.seconds.innerText = String(seconds);
                }
            }
        }, 1000);
    }

    /**
     * Stops the countdown timer.
     *
     * @returns {void}
     */
    stop() {
        clearInterval(
            /** @type {ReturnType<typeof setInterval>} */ (this.timerId),
        );
    }
}
